import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  HostListener,
  OnInit,
  ViewChild,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Permissions } from '@app/core/guard/Permissions';
import { ConfirmationService } from '@services/helpers/confirmation.service';
import { PermissionsService } from '@services/helpers/permissions.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { RichTextEditorComponent } from '@src/app/shared/components/rich-text-editor/rich-text-editor.component';
import { VaultDatePipe } from '@src/app/shared/pipes/vault-date.pipe';
import { VaultDateTimePipe } from '@src/app/shared/pipes/vault-date-time.pipe';
import { SharedModule } from '@src/app/shared/shared.module';
import { MenuItem } from 'primeng/api';
import { Observable, first } from 'rxjs';
import { PartnerContractPartyFormComponent } from '../../components/partner-contract-party-form/partner-contract-party-form.component';
import { PartnerContractUnsavedChanges } from '../../guards/partner-contract-unsaved-changes.guard';
import {
  backendErrorMessage,
  describeOutOfDefaultFields,
  describeUnfilledVariables,
  partnerContractNumber,
  partnerContractPartyTypeLabel,
  partnerContractRevisionActionLabel,
  partnerContractStatusLabel,
} from '../../utils/partner-contract.utils';
import {
  PartnerContractRevisionListItemResponse,
  PartnerContractRevisionResponse,
} from '@interfaces/partners/contracts/responses/partner-contract-revision.response';
import { findVariableKeys } from '@src/app/shared/components/rich-text-editor/rich-text-editor.commands';
import { PartnerContractRevisionHistoryComponent } from './components/partner-contract-revision-history/partner-contract-revision-history.component';
import { ModalPartnerContractPreviewComponent } from './components/modal-partner-contract-preview/modal-partner-contract-preview.component';
import { PartnerContractEditorState } from './partner-contract-editor.state';

/**
 * Editor da minuta do Contrato de Parceria Comercial de um parceiro (card 3462).
 *
 * Texto travado (card 3643): o centro mostra a prévia do texto renderizado pelo backend; quem muda o
 * texto são os dados da parte no painel lateral. Só quem está na lista de editores do texto vê
 * "Editar texto", que troca a prévia pelo editor do corpo-fonte. Painel lateral com os dados da parte
 * ou o histórico (revisões e alterações de valores), pré-visualização, PDF, finalizar/reabrir e
 * excluir. Finalizada, a minuta fica só leitura. O estado e as chamadas moram em
 * {@link PartnerContractEditorState}.
 */
@Component({
  selector: 'app-partner-contract-editor',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SharedModule,
    VaultDatePipe,
    VaultDateTimePipe,
    RichTextEditorComponent,
    PartnerContractPartyFormComponent,
    ModalPartnerContractPreviewComponent,
    PartnerContractRevisionHistoryComponent,
  ],
  templateUrl: './partner-contract-editor.component.html',
  styleUrls: ['./partner-contract-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [PartnerContractEditorState],
})
export class PartnerContractEditorComponent implements OnInit, PartnerContractUnsavedChanges {
  readonly state = inject(PartnerContractEditorState);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly permissionsService = inject(PermissionsService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly toast = inject(ToastHelperService);
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild('previewModal') private previewModal?: ModalPartnerContractPreviewComponent;

  readonly home: MenuItem = { icon: 'pi pi-home', routerLink: '/dashboard/dashboard-management' };

  readonly canUpdate = this.permissionSignal(Permissions.PartnerContract.UPDATE);
  readonly canFinalize = this.permissionSignal(Permissions.PartnerContract.FINALIZE);
  readonly canDelete = this.permissionSignal(Permissions.PartnerContract.DELETE);
  readonly canExportPdf = this.permissionSignal(Permissions.PartnerContract.EXPORT_PDF);

  /** Painel lateral: dados da parte, histórico de revisões ou recolhido. */
  readonly asidePanel = signal<'party' | 'history' | null>('party');
  /** Revisão aberta na pré-visualização (destaque na lista e "Usar este texto"). */
  readonly selectedRevisionId = signal<string | null>(null);
  private viewedRevision: PartnerContractRevisionResponse | null = null;
  readonly partyTypeLabel = partnerContractPartyTypeLabel;
  /** Título em edição no cabeçalho (o lápis troca o texto pelo campo). */
  readonly editingTitle = signal(false);

  /** Itens do menu "⋯" do cabeçalho: o que é raro ou destrutivo fica fora da barra. */
  readonly moreMenuItems: MenuItem[] = [
    { label: 'Excluir minuta', icon: 'pi pi-trash', command: () => this.confirmDelete() },
  ];

  /** Minuta travada: finalizada ou sem permissão de editar (título e dados da parte). */
  readonly locked = computed(() => this.state.isFinalized() || !this.canUpdate());
  /** "Editar texto" disponível: minuta destravada e usuário na lista de editores do texto. */
  readonly canEditTextNow = computed(() => !this.locked() && this.state.canEditText());
  /** Exemplo exibido no aviso do texto-fonte (no template, `{{` seria lido como interpolação). */
  readonly variableExample = '{{chave}}';
  /** Número do contrato (`PR-202610-500`), com o código antigo de reserva. */
  readonly contractNumber = computed(() => partnerContractNumber(this.state.contract()));
  /** Banner de aprovação: o backend marcou valor fora do padrão na última gravação. */
  readonly outOfDefaultDescription = computed(() =>
    this.state.contract()?.requiresApproval
      ? describeOutOfDefaultFields(this.state.contract()?.outOfDefaultFields)
      : ''
  );
  readonly statusLabel = computed(() => partnerContractStatusLabel(this.state.contract()?.status));
  readonly unfilledDescription = computed(() =>
    describeUnfilledVariables(this.state.unfilledKeys(), this.state.variables())
  );

  readonly breadcrumbs = computed<MenuItem[]>(() => {
    const contract = this.state.contract();
    return [
      { label: 'Gerenciamento' },
      { label: 'Parceiros', routerLink: '/dashboard/partner/list' },
      {
        label: contract?.partnerName || 'Parceiro',
        routerLink: contract ? `/dashboard/partner/register/${contract.partnerId}` : undefined,
        queryParams: { tab: 'contracts' },
      },
      { label: partnerContractNumber(contract) || 'Contrato' },
    ];
  });

  constructor() {
    // Trava/destrava título e dados da parte junto com o texto (o editor recebe `readonly`).
    effect(() => {
      const options = { emitEvent: false };
      if (this.locked()) {
        this.state.titleControl.disable(options);
        this.state.partyForm.disable(options);
      } else {
        this.state.titleControl.enable(options);
        this.state.partyForm.enable(options);
      }
    });
  }

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      this.router.navigate(['/dashboard/partner/list']);
      return;
    }

    this.state
      .load(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: contract => {
          if (!contract) {
            this.toast.showToastError('Contrato não encontrado', 'Ele pode ter sido excluído.');
            this.router.navigate(['/dashboard/partner/list']);
          }
        },
        error: error =>
          this.toast.showToastError(
            'Não foi possível abrir o contrato',
            backendErrorMessage(error) || 'Recarregue a página para tentar de novo.'
          ),
      });
  }

  canSave(): boolean {
    return !this.locked() && this.state.dirty() && !this.state.busy();
  }

  save(): void {
    if (!this.canSave()) return;
    if (!this.ensureValid()) return;

    this.state
      .save()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.toast.showToastSuccess('Contrato salvo', ''),
        error: error => this.showError('Não foi possível salvar o contrato', error),
      });
  }

  refill(): void {
    if (this.locked() || this.state.busy() || !this.ensureValid()) return;

    this.state
      .refill()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: contract => {
          const remaining = contract?.unfilledVariables?.length ?? 0;
          if (remaining) {
            this.toast.showToastWarn(
              'Ainda faltam dados',
              `Preencha no painel: ${describeUnfilledVariables(contract?.unfilledVariables ?? [], this.state.variables())}.`
            );
          } else {
            this.toast.showToastSuccess(
              'Variáveis preenchidas',
              'O texto já está com todos os dados.'
            );
          }
        },
        error: error => this.showError('Não foi possível preencher as variáveis', error),
      });
  }

  /**
   * Pré-visualiza o texto renderizado gravado; com "Editar texto" ligado, o corpo-fonte em edição
   * (com as variáveis destacadas).
   */
  openPreview(): void {
    const editingSource = this.state.textMode();
    const bodyHtml = editingSource
      ? this.state.sourceBodyControl.value
      : this.state.renderedBodyControl.value;
    this.previewModal?.open({
      title: this.state.titleControl.value,
      bodyHtml,
      unfilledKeys: editingSource ? findVariableKeys(bodyHtml ?? '') : this.state.unfilledKeys(),
      variables: this.state.variables(),
    });
  }

  /** "Editar texto" / "Ver prévia" (só editor do texto). */
  toggleTextMode(): void {
    if (!this.canEditTextNow()) return;
    this.state.toggleTextMode();
  }

  /** Baixa o PDF. Com mudança não salva (e permissão de editar), salva antes. */
  downloadPdf(): void {
    if (this.state.busy()) return;

    if (this.state.dirty() && !this.locked()) {
      if (!this.ensureValid()) return;
      this.state
        .save()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => this.runDownload(),
          error: error => this.showError('Não foi possível salvar antes de gerar o PDF', error),
        });
      return;
    }

    this.runDownload();
  }

  confirmFinalize(): void {
    if (this.state.busy() || !this.ensureValid()) return;
    const pending = this.unfilledDescription();

    this.confirmationService
      .confirm(
        'Finalizar o contrato?',
        (pending ? `Ainda faltam dados: ${pending}. ` : '') +
          'O texto fica travado; para mudar depois, use Reabrir.',
        pending ? 'danger' : 'default',
        'Cancelar',
        'Finalizar'
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(confirmed => {
        if (!confirmed) return;
        this.state
          .finalize()
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () =>
              this.toast.showToastSuccess(
                'Contrato finalizado',
                'O texto está travado para edição.'
              ),
            error: error => this.showError('Não foi possível finalizar o contrato', error),
          });
      });
  }

  reopen(): void {
    if (this.state.busy()) return;
    this.state
      .reopen()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () =>
          this.toast.showToastSuccess(
            'Contrato reaberto',
            'Ele voltou para rascunho e pode ser editado.'
          ),
        error: error => this.showError('Não foi possível reabrir o contrato', error),
      });
  }

  confirmDelete(): void {
    const contract = this.state.contract();
    if (!contract || this.state.busy()) return;

    this.confirmationService
      .confirm(
        `Excluir o contrato ${partnerContractNumber(contract)}?`,
        'A minuta sai da lista do parceiro. O modelo e os outros contratos não mudam.',
        'danger',
        'Cancelar',
        'Excluir'
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(confirmed => {
        if (!confirmed) return;
        this.state
          .delete()
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.toast.showToastSuccess('Contrato excluído', '');
              // Já foi excluído: sai sem a pergunta de "alterações não salvas".
              this.state.dirty.set(false);
              this.backToPartner();
            },
            error: error => this.showError('Não foi possível excluir o contrato', error),
          });
      });
  }

  backToPartner(): void {
    const partnerId = this.state.contract()?.partnerId;
    if (partnerId) {
      this.router.navigate(['/dashboard/partner/register', partnerId], {
        queryParams: { tab: 'contracts' },
      });
    } else {
      this.router.navigate(['/dashboard/partner/list']);
    }
  }

  togglePartyPanel(): void {
    this.asidePanel.update(panel => (panel === 'party' ? null : 'party'));
  }

  /** Abre o histórico no lugar dos dados da parte (e busca a lista); de novo, volta aos dados. */
  toggleHistory(): void {
    if (this.asidePanel() === 'history') {
      this.asidePanel.set('party');
      return;
    }
    this.asidePanel.set('history');
    this.state.requestRevisions();
  }

  /** Abre a pré-visualização com o texto da revisão escolhida. */
  openRevision(item: PartnerContractRevisionListItemResponse): void {
    this.state
      .getRevision(item.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: revision => {
          if (!revision) return;
          this.viewedRevision = revision;
          this.selectedRevisionId.set(revision.id);
          const author = revision.createdByName ? ` · ${revision.createdByName}` : '';
          this.previewModal?.open({
            title: revision.title,
            bodyHtml: revision.bodyHtml,
            unfilledKeys: findVariableKeys(revision.bodyHtml ?? ''),
            variables: this.state.variables(),
            revision: {
              label: `Revisão ${revision.revisionNumber} · ${partnerContractRevisionActionLabel(revision.action)}${author}`,
              // Só com o corpo-fonte da revisão (vem só para editor): restaura o texto com as variáveis.
              canUseText: this.canEditTextNow() && !!revision.sourceBodyHtml,
            },
          });
        },
        error: error =>
          this.showError(`Não foi possível abrir a revisão ${item.revisionNumber}`, error),
      });
  }

  /** "Usar este texto" da pré-visualização: leva título e corpo da revisão ao editor, sem salvar. */
  useRevisionText(): void {
    const revision = this.viewedRevision;
    if (!revision || !this.canEditTextNow() || !revision.sourceBodyHtml) return;

    const apply = () => {
      this.state.useRevisionText(revision);
      this.toast.showToastInfo(
        `Texto da revisão ${revision.revisionNumber} no editor`,
        'O texto volta com as variáveis, preenchidas pelos dados atuais da parte ao salvar. Revise e use Salvar para gravar.'
      );
    };

    if (!this.state.dirty()) {
      apply();
      return;
    }
    this.confirmationService
      .confirm(
        'Substituir o texto do editor?',
        'As mudanças ainda não salvas no título e no texto serão trocadas pelo texto da revisão.',
        'danger',
        'Cancelar',
        'Substituir'
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(confirmed => {
        if (confirmed) apply();
      });
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
      event.preventDefault();
      this.save();
    }
  }

  confirmLeave(): boolean | Observable<boolean> {
    if (!this.state.dirty()) return true;
    return this.confirmationService
      .confirm(
        'Sair sem salvar?',
        'As mudanças neste contrato ainda não foram salvas e serão perdidas.',
        'danger',
        'Continuar editando',
        'Sair sem salvar'
      )
      .pipe(first());
  }

  @HostListener('window:beforeunload', ['$event'])
  protected onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.state.dirty()) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  private runDownload(): void {
    this.state
      .downloadPdf()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ error: error => this.showError('Não foi possível gerar o PDF', error) });
  }

  /** Título, razão social e data são obrigatórios; diz onde corrigir. */
  private ensureValid(): boolean {
    if (this.state.isValid()) return true;
    this.state.partyForm.markAllAsTouched();
    this.state.titleControl.markAsTouched();
    // Título vazio fica escondido no cabeçalho compacto: abre o campo para a pessoa ver onde corrigir.
    if (this.state.titleControl.invalid) this.editingTitle.set(true);
    this.asidePanel.set('party');
    this.toast.showToastWarn(
      'Faltam dados obrigatórios',
      'Preencha o título e, no painel "Dados da parte", a razão social e a data do contrato (e confira o e-mail).'
    );
    return false;
  }

  private showError(title: string, error: unknown): void {
    this.toast.showToastError(title, backendErrorMessage(error) || 'Tente de novo em instantes.');
  }

  private permissionSignal(permission: string) {
    return toSignal(this.permissionsService.hasPermission(permission), { initialValue: false });
  }
}
